import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { defaultsSettingApi, settingApi, workspaceCurrencyApi } from "api/TenantSlug/Setting/api.ts";
import { composeSettingsQueryOptions } from "api/TenantSlug/Setting/queryOptions.ts";
import { AxiosError } from "axios";
import { CurrencyReconversionStatus } from "types/enums.ts";
import { ApiErrorResponse } from "types/models.ts";

import StoreSettingRequest = App.Http.Requests.Setting.StoreSettingRequest;
import UpdateWorkspaceCurrencyRequest = App.Http.Requests.Setting.UpdateWorkspaceCurrencyRequest;
import UpdateSettingsRequest = App.Http.Requests.Setting.UpdateSettingsRequest;
import GetSettingsResponse = App.Http.Responses.Setting.GetSettingsResponse;
import GetSettingResponse = App.Http.Responses.Setting.GetSettingResponse;
import GetSettingsDefaultsResponse = App.Http.Responses.Setting.GetSettingsDefaultsResponse;
import GetWorkspaceCurrencyResponse = App.Http.Responses.Setting.GetWorkspaceCurrencyResponse;

// Hook to get list of settings
export const useGetSettingsQuery = (options = {}) => {
    return useQueryApi<GetSettingsResponse>({
        ...composeSettingsQueryOptions(),
        ...options,
    });
};

// Hook to get a specific setting
export const useGetSettingQuery = (key: string, options = {}) => {
    return useQueryApi<GetSettingResponse>({
        queryKey: ["getSetting", key],
        queryFn: () => settingApi.show(key),
        ...options,
    });
};

// Hook to store a setting
export const useStoreSettingMutation = (
    options: UseMutationOptions<GetSettingResponse, AxiosError<ApiErrorResponse>, StoreSettingRequest> = {}
) => {
    return useMutationApi<GetSettingResponse, StoreSettingRequest>({
        mutationKey: ["storeSetting"],
        mutationFn: (request: StoreSettingRequest) => settingApi.store(request),
        ...options,
    });
};

// Hook to update settings
export const useUpdateSettingMutation = (
    options: UseMutationOptions<GetSettingsResponse, AxiosError<ApiErrorResponse>, UpdateSettingsRequest> = {}
) => {
    return useMutationApi<GetSettingsResponse, UpdateSettingsRequest>({
        mutationKey: ["updateSetting"],
        mutationFn: (request: UpdateSettingsRequest) => settingApi.update(request),
        ...options,
    });
};

// Hook to delete a setting
export const useDestroySettingMutation = (
    options: UseMutationOptions<GetSettingResponse, AxiosError<ApiErrorResponse>, string> = {}
) => {
    return useMutationApi<GetSettingResponse, string>({
        mutationKey: ["destroySetting"],
        mutationFn: (key: string) => settingApi.destroy(key),
        ...options,
    });
};

// Hook to get default settings
export const useGetDefaultsQuery = (tenantSlug: string, options = {}) => {
    return useQueryApi<GetSettingsDefaultsResponse>({
        queryKey: ["settings:defaults", tenantSlug],
        queryFn: () => defaultsSettingApi.defaults(tenantSlug),
        ...options,
    });
};

// Hook to get workspace currency + reconversion/cooldown status. Callers that need to react to
// a currently running reconversion (e.g. the report layout lock banner) should pass a
// `refetchInterval` so this keeps polling until the job finishes.
export const useGetWorkspaceCurrencyQuery = (options = {}) => {
    return useQueryApi<GetWorkspaceCurrencyResponse>({
        queryKey: ["getWorkspaceCurrency"],
        queryFn: () => workspaceCurrencyApi.show(),
        ...options,
    });
};

export const isWorkspaceCurrencyReconverting = (data?: GetWorkspaceCurrencyResponse): boolean =>
    data?.reconversion_status === CurrencyReconversionStatus.Pending ||
    data?.reconversion_status === CurrencyReconversionStatus.Running;

// Hook to update workspace currency (triggers a background report reconversion on the BE)
export const useUpdateWorkspaceCurrencyMutation = (
    options: UseMutationOptions<
        GetWorkspaceCurrencyResponse,
        AxiosError<ApiErrorResponse>,
        UpdateWorkspaceCurrencyRequest
    > = {}
) => {
    return useMutationApi<GetWorkspaceCurrencyResponse, UpdateWorkspaceCurrencyRequest>({
        mutationKey: ["updateWorkspaceCurrency"],
        mutationFn: (request: UpdateWorkspaceCurrencyRequest) => workspaceCurrencyApi.update(request),
        ...options,
    });
};
